import React, {useState, useRef, useEffect} from "react";
import {Spinner} from "../Spinner";
import {FaIcon} from "../FaIcon";
import {Switch} from "../buttons/Swtich";
import {renderMarkdown} from "../agent/renderMarkdown";


function formatDate(isoDate, t, language) {
    if (!isoDate) return t('client_reports.date_unknown');
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return t('client_reports.date_unknown');
    return d.toLocaleDateString(language) + ' ' + d.toLocaleTimeString(language, {hour: '2-digit', minute: '2-digit'});
}

function getStatusModifier(report) {
    if (report.status === 'PROCESSING') return 'processing';
    if (report.status === 'FAILED') return 'failed';
    return 'completed';
}

function renderStatusBadge(report, t) {
    const modifier = getStatusModifier(report);
    if (report.status === 'PROCESSING') {
        return (
            <span className={`client-report-status-badge client-report-status-badge--${modifier}`}>
                <Spinner specialClass="lds-dual-ring very-small" color="positive"/>
                {t('client_reports.status_processing')}
            </span>
        );
    }
    return (
        <span className={`client-report-status-badge client-report-status-badge--${modifier}`}>
            {report.status === 'FAILED' ? t('client_reports.status_failed') : t('client_reports.status_completed')}
        </span>
    );
}

function getOriginInfo(report, t) {
    const originType = report.origin_type;
    if (originType === 'ADVANCE_REPORTER') {
        const tooltip = report.user_query
            ? t('reports.origin_user_query', {query: report.user_query})
            : t('reports.origin_advance_reporter');
        return {icon: 'robot', label: t('reports.origin_advance_reporter'), tooltip};
    }
    if (originType === 'TEMPLATE') {
        const label = report.template_name
            ? t('reports.origin_template_with_name', {name: report.template_name})
            : t('reports.origin_template');
        return {icon: 'file-chart-line', label, tooltip: label};
    }
    return {icon: 'wand-magic-sparkles', label: t('reports.origin_unknown'), tooltip: t('reports.origin_unknown')};
}

function getVisibilityState(report) {
    const clientVisible = report.is_visible_to_clients;
    const publicVisible = report.is_public;
    if (clientVisible && publicVisible) return 'all-visible';
    if (!clientVisible && !publicVisible) return 'private';
    return 'partial';
}

function getVisibilityIcon(report) {
    const state = getVisibilityState(report);
    if (state === 'all-visible') return 'eye';
    if (state === 'private') return 'lock';
    return 'eye-low-vision';
}

function getVisibilityLabel(report, t) {
    const state = getVisibilityState(report);
    if (state === 'all-visible') return t('reports.visibility_all_visible');
    if (state === 'private') return t('reports.visibility_private');
    if (!report.is_visible_to_clients) return t('reports.hidden_from_clients');
    return t('reports.visibility_no_public_link');
}


export function ReportCard({report, onDelete, onCancel, onToggleVisibility, showClientName, onClientNameClick, t, language}) {
    const [visibilityOpen, setVisibilityOpen] = useState(false);
    const visibilityRef = useRef(null);

    useEffect(() => {
        if (!visibilityOpen) return;
        const handleClickOutside = (e) => {
            if (visibilityRef.current && !visibilityRef.current.contains(e.target)) {
                setVisibilityOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [visibilityOpen]);

    const statusModifier = getStatusModifier(report);
    const hasSummary = report.summary && report.summary.trim().length > 0 && report.status !== 'PROCESSING';
    const isClientNameClickable = showClientName && report.client_name && report.client_id && onClientNameClick;
    const hasVisibility = report.is_visible_to_clients !== undefined && report.status !== 'PROCESSING';

    const renderClientName = () => {
        if (!showClientName || !report.client_name) return null;
        if (isClientNameClickable) {
            return (
                <button
                    type="button"
                    className="client-report-client-name client-report-client-name--clickable"
                    onClick={() => onClientNameClick(report.client_id, report.client_name)}
                    title={t('client_profile.edit_hint')}
                >
                    <FaIcon iconName="pen-to-square" className="client-report-client-name-icon" />
                    <span className="client-report-client-name-text">{report.client_name}</span>
                </button>
            );
        }
        return <span className="client-report-client-name">{report.client_name}</span>;
    };

    return (
        <div className={`client-report-card client-report-card--${statusModifier}${visibilityOpen ? ' client-report-card--popover-open' : ''}`}>
            <div className="client-report-icon">
                <FaIcon iconName="file-lines" />
            </div>
            <div className="client-report-content">
                <div className="client-report-header-row">
                    <h3 className="client-report-title">{report.title}</h3>
                    <div className="client-report-actions">
                        {report.status === 'COMPLETED' && report.url && (
                            <a
                                href={report.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="client-report-view-link"
                            >
                                <FaIcon iconName="external-link" />
                                {t('client_reports.view_report')}
                            </a>
                        )}
                        {report.status === 'PROCESSING' && (
                            <button
                                type="button"
                                className="client-report-cancel-btn"
                                onClick={() => onCancel(report.report_id)}
                            >
                                {t('client_reports.cancel')}
                            </button>
                        )}
                        {report.status !== 'PROCESSING' && (
                            <button
                                type="button"
                                className={`client-report-delete-btn${report.can_delete === false ? ' client-report-delete-btn--disabled' : ''}`}
                                onClick={report.can_delete !== false ? () => onDelete(report.report_id) : undefined}
                                disabled={report.can_delete === false}
                                title={report.can_delete === false
                                    ? t('client_reports.delete_no_permission', {name: report.created_by_name || t('client_reports.unknown_user')})
                                    : t('client_reports.delete_confirm')
                                }
                            >
                                <FaIcon iconName="trash" className="client-report-delete-icon" />
                            </button>
                        )}
                    </div>
                </div>
                {hasSummary && (
                    <div
                        className="client-report-summary"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(report.summary) }}
                    />
                )}
                <div className="client-report-meta">
                    {renderClientName()}
                    {(() => {
                        const origin = getOriginInfo(report, t);
                        return (
                            <span
                                className={`client-report-origin-badge -${(report.origin_type || 'unknown').toLowerCase()}`}
                                title={origin.tooltip}
                            >
                                <FaIcon iconName={origin.icon} />
                                <span className="client-report-origin-label">{origin.label}</span>
                            </span>
                        );
                    })()}
                    <span className="client-report-date">{formatDate(report.generated_at, t, language)}</span>
                    {renderStatusBadge(report, t)}
                    {report.created_by_name && report.status !== 'PROCESSING' && (
                        <span className="client-report-author">
                            {t('all_reports.by_author', {name: report.created_by_name})}
                        </span>
                    )}
                    {hasVisibility && onToggleVisibility && (
                        <div className="client-report-visibility" ref={visibilityRef}>
                            <button
                                type="button"
                                className={`client-report-visibility-trigger -${getVisibilityState(report)}`}
                                onClick={() => setVisibilityOpen(!visibilityOpen)}
                            >
                                <FaIcon iconName={getVisibilityIcon(report)} />
                                {getVisibilityLabel(report, t)}
                            </button>
                            {visibilityOpen && (
                                <div className="client-report-visibility-popover">
                                    <div className="visibility-popover-header">
                                        <span>{t('reports.visibility_title')}</span>
                                    </div>
                                    <div className="visibility-popover-body">
                                        <div className="visibility-popover-row">
                                            <div className="visibility-popover-icon">
                                                <FaIcon iconName="users" />
                                            </div>
                                            <div className="visibility-popover-info">
                                                <span className="visibility-popover-label">{t('reports.visible_to_clients_label')}</span>
                                                <span className="visibility-popover-hint">{t('reports.visible_to_clients_popover_hint')}</span>
                                            </div>
                                            <Switch
                                                name={`vis-clients-${report.report_id}`}
                                                checked={report.is_visible_to_clients}
                                                onChange={(e) => onToggleVisibility(report.report_id, {is_visible_to_clients: e.target.checked})}
                                                style={{margin: 0}}
                                            />
                                        </div>
                                        <div className="visibility-popover-row">
                                            <div className="visibility-popover-icon">
                                                <FaIcon iconName="link" />
                                            </div>
                                            <div className="visibility-popover-info">
                                                <span className="visibility-popover-label">{t('reports.public_link_label')}</span>
                                                <span className="visibility-popover-hint">{t(report.is_public ? 'reports.public_link_hint' : 'reports.public_link_off_hint')}</span>
                                            </div>
                                            <Switch
                                                name={`vis-public-${report.report_id}`}
                                                checked={report.is_public}
                                                onChange={(e) => onToggleVisibility(report.report_id, {is_public: e.target.checked})}
                                                style={{margin: 0}}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
